import {
  exportPrisma,
  prisma,
  reportingPrisma,
  type Prisma,
  OrderStatus,
  BookableItemTranslations,
  TicketCategory,
  ResourceType,
  ActionType,
  DesignTemplateType,
  Privacies,
  OrderItemStatus,
  BookableItemType,
} from "client";
import { getTicketQuantity } from "services/ticketQuantity";
import { buildPaymentSettlementExpressions } from "services/virtualOrderStatus";
import { createBookableItem, updateBookableItem } from "../bookableItem";
import { invalidateEventPageCacheForEvent } from "../event/eventPageCache";
import {
  flattenTicketWithBookableItem,
  separateTicketFields,
} from "../bookableItem/helpers";

/**
 * ==============================================================================
 * TICKET SERVICE - BOOKABLE ITEM MIGRATION (COMPLETED ✅)
 * ==============================================================================
 *
 * All ticket service functions have been migrated to use the BookableItem
 * architecture. All shared fields (price, dates, translations, designs, etc.)
 * are now accessed via ticket.bookableItem.
 *
 * ✅ ALL MIGRATIONS COMPLETED:
 *
 * **Write Operations:**
 * - createTicket() - Creates BookableItem first, then links Ticket
 * - updateTicket() - Updates BookableItem and Ticket separately
 * - deleteTicket() - Soft deletes both Ticket and BookableItem in transaction
 * - endTicketSale() - Updates BookableItem.saleEndDate
 *
 * **Read Operations:**
 * - getTicket() - Returns flattened structure
 * - getEventTickets() - Returns flattened structure
 * - getEventTicketsWithTemplates() - Uses bookableItem.designs
 * - getInvitationsEventTickets() - Returns flattened structure
 * - getTicketsByEventId() - Returns flattened structure
 * - getTicketWithCategory() - Returns flattened structure
 * - getTicketsFromEvents() - Returns flattened structure with privacy filtering
 * - getTicketTemplateByOrderId() - Uses bookableItem.designs
 * - getTicketTemplatesByTicketId() - Uses bookableItem.designs
 *
 * **Complex Aggregations:**
 * - getTicketGroupsWithTickets() - MongoDB aggregation (needs manual migration)
 * - ticketByRequestReport() - MongoDB aggregation (needs manual migration)
 *
 * 📝 BACKWARD COMPATIBILITY:
 * - All functions return flattened structures using flattenTicketWithBookableItem()
 * - Existing API endpoints will receive the same data structure
 * - FormEntities moved from ticket.formEntity to bookableItem.formEntities
 * - Designs accessed via bookableItem.designs (DesignOnTickets removed)
 *
 * 🔄 FIELD MAPPING:
 * Old Field                  → New Field
 * ticket.price               → ticket.bookableItem.price
 * ticket.saleStartDate       → ticket.bookableItem.saleStartDate
 * ticket.saleEndDate         → ticket.bookableItem.saleEndDate
 * ticket.translations        → ticket.bookableItem.translations
 * ticket.designs             → ticket.bookableItem.designs
 * ticket.privacy             → ticket.bookableItem.privacy
 * ticket.requireApproval     → ticket.bookableItem.requireApproval
 * ticket.formEntity          → ticket.bookableItem.formEntities
 * orderItem.invitation       → orderItem.invitationAttendee.invitation
 *
 * 📖 For detailed migration documentation, see:
 * - TICKET_SERVICE_MIGRATION_GUIDE.md
 * - PHASE2_BACKEND_CHANGES.md
 *
 * ==============================================================================
 */

/**
 * Get ticket template by order ID (MIGRATED)
 * Uses bookableItem.designs instead of ticketTemplates
 * Returns old structure for backward compatibility
 */
export const getTicketTemplateByOrderId = async (orderId: string) => {
  try {
    const order = await prisma.order.findUnique({
      where: {
        id: orderId,
      },
      include: {
        formResults: {
          include: { formResult: { include: { formTemplate: true } } },
        },
        event: {
          select: {
            slug: true,
            whiteLabel: { select: { subdomain: true } },
          },
        },
        orderItems: {
          include: {
            invitationAttendee: {
              include: {
                invitation: {
                  include: {
                    translations: true,
                    designs: {
                      where: {
                        design: {
                          type: DesignTemplateType.PRINTABLE_INVITATION,
                        },
                      },
                      include: {
                        design: true,
                      },
                    },
                  },
                },
              },
            },
            ticket: {
              include: {
                bookableItem: {
                  include: {
                    designs: true,
                    translations: true,
                  },
                },
              },
            },
          },
        },
      },
    });

    if (!order) return null;

    // Transform to old structure for backward compatibility
    return {
      ...order,
      orderItems: order.orderItems.map((item) => {
        // Map invitationAttendee.invitation to direct invitation (old structure)
        const invitation = item.invitationAttendee?.invitation || null;

        // Map ticket.bookableItem.designs to ticketTemplates (old structure)
        const ticket = item.ticket
          ? {
              ...flattenTicketWithBookableItem(item.ticket as any),
              ticketTemplates: item.ticket.bookableItem.designs?.map(
                (design: any) => ({
                  design,
                  designId: design.id,
                })
              ),
            }
          : null;

        return {
          ...item,
          invitation, // Old structure: direct invitation
          ticket, // Old structure: ticket with ticketTemplates
        };
      }),
    };
  } catch (error: any) {
    throw error;
  }
};
/**
 * Get invitation tickets for an event (MIGRATED)
 * Returns flattened structure for backward compatibility
 */
export const getInvitationsEventTickets = async (
  userId: string,
  eventId: string,
  lang?: string
) => {
  const tickets = await prisma.ticket.findMany({
    where: {
      AND: [{ eventId }, { deletedAt: { isSet: false } }],
      bookableItem: {
        privacy: Privacies.INVITATION,
      },
    },
    include: {
      bookableItem: {
        include: {
          translations: lang
            ? {
                where: {
                  language: {
                    code: lang,
                  },
                },
              }
            : true,
          formEntities: true,
        },
      },
    },
  });

  return tickets.map((ticket) => {
    const flat = flattenTicketWithBookableItem(ticket as any);
    return lang
      ? {
          ...flat,
          ...flat.translations?.[0],
          id: ticket.id,
          hasForm: !!flat.formEntities?.length,
        }
      : flat;
  });
};
/**
 * Get event tickets (MIGRATED)
 * Returns flattened structure for backward compatibility
 */
export const getEventTickets = async (
  userId: string,
  eventId: string,
  lang?: string,
  all?: boolean
) => {
  try {
    //TODO : open this service for door management users

    const whereClause: any = {
      AND: [{ eventId }, { deletedAt: { isSet: false } }],
    };

    if (!all) {
      whereClause.bookableItem = {
        price: { gt: 0 },
      };
    }

    const tickets = await prisma.ticket.findMany({
      where: whereClause,
      include: {
        bookableItem: {
          include: {
            translations: lang
              ? {
                  where: {
                    language: {
                      code: lang,
                    },
                  },
                }
              : true,
            formEntities: {
              include: {
                formTemplate: true,
              },
            },
          },
        },
      },
    });

    return tickets.map((ticket) => {
      const flat = flattenTicketWithBookableItem(ticket as any);
      return lang
        ? {
            ...flat,
            ...flat.translations?.[0],
            id: ticket.id,
            hasForm: !!flat.formEntities?.length,
          }
        : flat;
    });
  } catch (error: any) {
    console.error(error);
    throw error;
  }
};

/**
 * Get event tickets with design templates (MIGRATED)
 * Uses bookableItem.designs instead of ticketTemplates
 * Returns old structure with ticketTemplates for backward compatibility
 */
export const getEventTicketsWithTemplates = async (
  eventId: string,
  lang?: string
) => {
  const tickets = await prisma.ticket.findMany({
    where: {
      AND: [{ eventId }, { deletedAt: { isSet: false } }],
    },
    include: {
      bookableItem: {
        include: {
          translations: lang
            ? {
                where: {
                  language: {
                    code: lang,
                  },
                },
              }
            : true,
          designs: true,
          formEntities: true,
        },
      },
    },
  });

  return tickets.map((ticket) => {
    const flat = flattenTicketWithBookableItem(ticket as any);

    // Create ticketTemplates array from bookableItem.designs (old structure)
    const ticketTemplates =
      flat.designs?.map((design: any) => ({
        ticket,
        design,
        ticketId: ticket.id,
        designId: design.id,
      })) || [];

    // Check designs from bookableItem
    const hasDigitalBadge = !!ticketTemplates.find(
      (template: any) =>
        template.design.type === DesignTemplateType.DIGITAL_BADGE
    );

    const hasPrintableBadge = !!ticketTemplates.find(
      (template: any) =>
        template.design.type === DesignTemplateType.PRINTABLE_BADGE
    );

    const hasDigitalTicketTemplate = !!ticketTemplates.find(
      (template: any) =>
        template.design.type === DesignTemplateType.DIGITAL_TICKET
    );

    const hasPrintableTicketTemplate = !!ticketTemplates.find(
      (template: any) =>
        template.design.type === DesignTemplateType.PRINTABLE_TICKET
    );

    return lang
      ? {
          ...flat,
          ...flat.translations?.[0],
          id: ticket.id,
          ticketTemplates, // Old structure: array of {ticket, design}
          hasDigitalBadge,
          hasPrintableBadge,
          hasDigitalTicketTemplate,
          hasPrintableTicketTemplate,
        }
      : {
          ...flat,
          ticketTemplates, // Old structure: array of {ticket, design}
          hasDigitalBadge,
          hasPrintableBadge,
          hasDigitalTicketTemplate,
          hasPrintableTicketTemplate,
        };
  });
};

export const getEventBookableItemsWithTemplates = async (
  eventId: string,
  lang?: string
) => {
  const bookableItems = await prisma.bookableItem.findMany({
    where: { eventId },
    include: {
      translations: lang
        ? {
            where: {
              language: {
                code: lang,
              },
            },
          }
        : true,
      designs: true,
      formEntities: true,
      ticket: {
        select: {
          id: true,
        },
      },
    },
  });

  return bookableItems.map((bookableItem) => {
    // Create templates array from bookableItem.designs (old structure)
    const templates =
      bookableItem.designs?.map((design: any) => ({
        bookableItem,
        design,
        bookableItemId: bookableItem.id,
        designId: design.id,
      })) || [];

    // Check designs from bookableItem
    const hasDigitalBadge = !!templates.find(
      (template: any) =>
        template.design.type === DesignTemplateType.DIGITAL_BADGE
    );

    const hasPrintableBadge = !!templates.find(
      (template: any) =>
        template.design.type === DesignTemplateType.PRINTABLE_BADGE
    );

    const hasDigitalTicketTemplate = !!templates.find(
      (template: any) =>
        template.design.type === DesignTemplateType.DIGITAL_TICKET
    );

    const hasPrintableTicketTemplate = !!templates.find(
      (template: any) =>
        template.design.type === DesignTemplateType.PRINTABLE_TICKET
    );

    return {
      ...bookableItem.translations?.[0],
      ...bookableItem,
      templates,
      hasDigitalBadge,
      hasPrintableBadge,
      hasDigitalTicketTemplate,
      hasPrintableTicketTemplate,
    };
  });
};
/**
 * Get single ticket by ID (MIGRATED)
 * Returns flattened structure for backward compatibility
 */
export const getTicket = async (id: string, languageId?: string) => {
  try {
    const ticket = await prisma.ticket.findFirst({
      where: { id, deletedAt: { isSet: false } },
      include: {
        bookableItem: {
          include: {
            translations: {
              where: languageId ? { languageId } : undefined,
            },
          },
        },
      },
    });

    if (!ticket) return null;

    return flattenTicketWithBookableItem(ticket as any);
  } catch (error: any) {
    throw error;
  }
};

/**
 * Get tickets by event ID (MIGRATED)
 * Returns flattened structure for backward compatibility
 */
export const getTicketsByEventId = async (
  userId: string,
  eventId: string,
  lang?: string
) => {
  try {
    const tickets = await prisma.ticket.findMany({
      where: { AND: [{ eventId }, { deletedAt: { isSet: false } }] },
      include: {
        bookableItem: {
          include: {
            translations: lang
              ? {
                  where: {
                    language: {
                      code: lang,
                    },
                  },
                }
              : true,
          },
        },
      },
    });

    return tickets.map((ticket) =>
      flattenTicketWithBookableItem(ticket as any)
    );
  } catch (error: any) {
    console.error(error);
    throw error;
  }
};

/**
 * Get ticket with category (MIGRATED)
 * Returns flattened structure for backward compatibility
 */
export const getTicketWithCategory = async (id: string) => {
  try {
    const ticket = await prisma.ticket.findFirst({
      where: { id, deletedAt: { isSet: false } },
      include: {
        category: true,
        bookableItem: {
          include: {
            translations: true,
          },
        },
      },
    });

    if (!ticket) return null;

    return flattenTicketWithBookableItem(ticket as any);
  } catch (error: any) {
    throw error;
  }
};

export type TicketWithCategory = {
  _id: string;
  categoryName: string;
  tickets: any[];
  category: TicketCategory;
  id?: string;
  isShared?: boolean;
  categoryQuantity?: number;
  reservedTicketOverCategory?: number;
  availableTicketOverCategory?: number;
  completedOrderOverCategory?: number;
  // [key in string]?: InputJsonValue;
};

export type InputJsonObject = { [Key in string]?: InputJsonValue };
export interface InputJsonArray extends Array<InputJsonValue> {}

export type InputJsonValue =
  | string
  | number
  | boolean
  | InputJsonObject
  | InputJsonArray;

export const getTicketGroupsWithTickets = async (
  userId: string,
  eventId: string,
  lang?: string,
  isDTCMEvent?: boolean
) => {
  try {
    // Heavy admin read aggregation; keep it off the transactional checkout/check-in circuit.
    const prisma = reportingPrisma;
    const stages = [
      {
        $facet: {
          ticketGroup: [
            {
              $match: {
                eventId: { $oid: eventId },
              },
            },
            {
              $lookup: {
                from: "TicketCategory",
                localField: "eventId",
                foreignField: "eventId",
                as: "category",
              },
            },
            { $unwind: "$category" },
            {
              $group: {
                _id: "$category._id",
                category: { $first: "$category" },
              },
            },
            {
              $lookup: {
                from: "Ticket",
                localField: "category._id",
                foreignField: "categoryId",
                as: "tickets",
              },
            },

            {
              $addFields: {
                hasTickets: { $gt: [{ $size: "$tickets" }, 0] },
              },
            },
            {
              $match: {
                hasTickets: false,
              },
            },
            {
              $addFields: {
                "category.id": { $toString: "$category._id" },
              },
            },
            ...(isDTCMEvent
              ? [
                  {
                    $lookup: {
                      from: "DTCMSection",
                      localField: "category._id",
                      foreignField: "ticketCategoryId",
                      as: "category.dtcm",
                    },
                  },
                ]
              : []),
            {
              $project: {
                _id: 0,
                category: 1,
              },
            },
          ],
          result: [
            {
              $match: {
                eventId: { $oid: eventId },
                deletedAt: { $exists: false },
              },
            },
            { $sort: { _id: -1 } },
            {
              $lookup: {
                from: "BookableItem",
                localField: "bookableItemId",
                foreignField: "_id",
                as: "bookableItem",
              },
            },
            {
              $unwind: {
                path: "$bookableItem",
                preserveNullAndEmptyArrays: true,
              },
            },
            {
              $addFields: {
                id: { $toString: "$_id" },
                ticketGroupId: { $toString: "$categoryId" },
                saleStartDate: { $toString: "$bookableItem.saleStartDate" },
                saleEndDate: { $toString: "$bookableItem.saleEndDate" },
                maxAmount: "$bookableItem.maxAmount",
                minAmount: "$bookableItem.minAmount",
                soldTogether: "$bookableItem.soldTogether",
                price: "$bookableItem.price",
                quantity: "$bookableItem.quantity",
                shareQuantityAmongTickets:
                  "$bookableItem.shareQuantityAmongTickets",
                privacy: "$bookableItem.privacy",
                requireApproval: "$bookableItem.requireApproval",
                showRemainingQuantity: "$bookableItem.showRemainingQuantity",
                showBeforeSaleStart: "$bookableItem.showBeforeSaleStart",
                showAfterSaleEnd: "$bookableItem.showAfterSaleEnd",
                showWhenSoldOut: "$bookableItem.showWhenSoldOut",
                color: "$bookableItem.color",
                disposition: "$bookableItem.disposition",
                icon: "$bookableItem.icon",
                externalLink: "$bookableItem.externalLink",
                taxable: "$bookableItem.taxable",
                isFree: "$bookableItem.isFree",
                enableExternalLink: "$bookableItem.enableExternalLink",
                token: "$bookableItem.token",
                showPriceToggle: "$bookableItem.showPriceToggle",
                showSaleEndsOnToggle: "$bookableItem.showSaleEndsOnToggle",
              },
            },
            {
              $lookup: {
                from: "OrderItem",
                localField: "_id",
                foreignField: "ticketId",
                as: "orderItems",
              },
            },
            {
              $addFields: {
                totalOrderItemsCount: { $size: "$orderItems" },
                "ticket.totalOrderItemsCount": { $size: "$orderItems" },
              },
            },
            {
              $unwind: {
                path: "$orderItems",
                preserveNullAndEmptyArrays: true,
              },
            },
            {
              $lookup: {
                from: "Order",
                localField: "orderItems.orderId",
                foreignField: "_id",
                as: "orders",
              },
            },
            { $unwind: { path: "$orders", preserveNullAndEmptyArrays: true } },
            {
              $group: {
                _id: {
                  status: "$orders.status",
                  ticketId: "$_id",
                  orderItems: "$orderItems.ticketId",
                },
                ticket: { $first: "$$ROOT" },
                count: { $count: {} },
              },
            },
            {
              $project: {
                _id: 0,
                ticket: 1,
                status: "$_id.status",
                statusQuantity: "$count",
              },
            },

            {
              $group: {
                _id: "$ticket._id",
                ticket: { $first: "$$ROOT" },
                reservedTicket: {
                  $sum: {
                    $cond: {
                      if: { $eq: ["$status", OrderStatus.ON_GOING] },
                      then: "$statusQuantity",
                      else: 0,
                    },
                  },
                },
                completedOrder: {
                  $sum: {
                    $sum: {
                      $cond: {
                        if: { $eq: ["$status", OrderStatus.COMPLETED] },
                        then: "$statusQuantity",
                        else: 0,
                      },
                    },
                  },
                },
              },
            },
            {
              $project: {
                _id: 0,
                ticket: "$ticket.ticket",
                status: "$_id.status",
                statusQuantity: "$count",

                ticketQuantity: {
                  availableTicket: {
                    $subtract: [
                      "$ticket.bookableItem.quantity",
                      {
                        $add: ["$reservedTicket", "$completedOrder"],
                      },
                    ],
                  },
                  completedOrder: "$completedOrder",
                  reservedTicket: "$reservedTicket",
                },
              },
            },
            {
              $lookup: {
                from: "TicketCategory",
                localField: "ticket.categoryId",
                foreignField: "_id",
                as: "category",
              },
            },
            {
              $unwind: { path: "$category", preserveNullAndEmptyArrays: true },
            },
            ...(isDTCMEvent
              ? [
                  {
                    $lookup: {
                      from: "DTCMSection",
                      localField: "category._id",
                      foreignField: "ticketCategoryId",
                      as: "category.dtcm",
                    },
                  },
                  {
                    $lookup: {
                      from: "DTCMTicketPrice",
                      localField: "ticket.dtcmTicketPriceId",
                      foreignField: "_id",
                      as: "ticket.dtcmTicket",
                    },
                  },
                  { $unwind: "$ticket.dtcmTicket" },
                ]
              : []),
            {
              $addFields: {
                categoryId: { $toString: "$categoryId" },
              },
            },
            {
              $lookup: {
                from: "BookableItemTranslations",
                localField: "ticket.bookableItemId",
                foreignField: "bookableItemId",
                as: "ticket.translations",
                pipeline: [
                  {
                    $lookup: {
                      from: "Language",
                      localField: "languageId",
                      foreignField: "_id",
                      as: "language",
                    },
                  },
                  {
                    $addFields: {
                      id: { $toString: "$_id" },
                      languageId: { $toString: "$languageId" },
                    },
                  },
                ],
              },
            },
            {
              $addFields: {
                "ticket.name": {
                  $arrayElemAt: ["$ticket.translations.name", 0],
                },
                "ticket.description": {
                  $arrayElemAt: ["$ticket.translations.description", 0],
                },
              },
            },
            { $sort: { "ticket._id": -1 } },
            {
              $group: {
                _id: "$category._id",
                categoryName: { $first: "$category.name" },
                category: { $first: "$category" },

                tickets: { $push: "$$ROOT" },
              },
            },
            {
              $addFields: {
                "category.id": { $toString: "$category._id" },
              },
            },
            {
              $project: {
                _id: 1,
                categoryName: 1,
                tickets: 1,
                category: 1,
              },
            },
          ],
        },
      },
    ];
    const tickets = (await prisma.ticket.aggregateRaw({
      pipeline: [...stages],
    })) as unknown as { ticketGroup: any[]; result: any[] }[];
    const concatenatedResult = tickets[0]?.ticketGroup?.concat(
      tickets[0].result
    );

    return concatenatedResult;
  } catch (e) {
    console.error(e);
  }
};

// CREATE
export const createTicket = async (
  userId: string,
  eventId: string,
  ticketPayload: any, // Changed from Prisma.TicketCreateInput as it now has mixed fields
  ticketData: BookableItemTranslations[], // Changed from TicketTranslations
  categoryId?: string
) => {
  try {
    // Separate BookableItem fields from Ticket-specific fields
    const { bookableItemData, ticketData: ticketSpecificData } =
      separateTicketFields(ticketPayload);

    // Create BookableItem first
    const bookableItem = await createBookableItem({
      type: BookableItemType.TICKET,
      eventId,
      ...bookableItemData,
    });

    // Create translations for BookableItem
    if (ticketData && ticketData.length > 0) {
      await prisma.bookableItemTranslations.createMany({
        data: ticketData.map((data) => ({
          ...data,
          bookableItemId: bookableItem.id,
        })),
      });
    }

    // Prepare ticket create data
    let ticketCreateData: any = {
      ...ticketSpecificData,
      bookableItem: {
        connect: {
          id: bookableItem.id,
        },
      },
      event: {
        connect: {
          id: eventId,
        },
      },
    };

    if (categoryId) {
      ticketCreateData.category = {
        connect: {
          id: categoryId,
        },
      };
    }

    const ticket = await prisma.ticket.create({
      data: ticketCreateData,
      include: {
        bookableItem: {
          include: {
            translations: true,
            designs: true,
            zones: true,
            participantGroups: true,
          },
        },
      },
    });

    await invalidateEventPageCacheForEvent(eventId);

    // Return flattened structure for backward compatibility
    return flattenTicketWithBookableItem(ticket as any);
  } catch (error: any) {
    throw new Error(error);
  }
};
// UPDATE
export const updateTicket = async (
  userId: string,
  id: string,
  updatedData: any, // Changed to any since it contains mixed fields
  ticketData: BookableItemTranslations[], // Changed from TicketTranslations
  categoryId?: string
) => {
  try {
    // Get the current ticket with its bookableItem
    const currentTicket = await prisma.ticket.findUnique({
      where: { id },
      include: { bookableItem: true },
    });

    if (!currentTicket) {
      throw new Error("Ticket not found");
    }

    // Separate BookableItem fields from Ticket-specific fields
    const { bookableItemData, ticketData: ticketSpecificData } =
      separateTicketFields(updatedData);

    // Validate quantity and price
    if (bookableItemData.quantity && Number(bookableItemData.quantity) < 0)
      throw new Error("quantity must be greater than 0");
    if (bookableItemData.price && Number(bookableItemData.price) < 0)
      throw new Error("price must be greater than 0");

    const ticketInfo = await getTicketQuantity(id);
    const reservedTicket = ticketInfo?.reservedTicket;

    if (
      bookableItemData.quantity &&
      reservedTicket !== undefined &&
      reservedTicket > Number(bookableItemData.quantity || 0)
    )
      throw new Error(`quantity must be greater than ${reservedTicket}`);

    // Update BookableItem if there are changes
    if (Object.keys(bookableItemData).length > 0) {
      await updateBookableItem(currentTicket.bookableItemId, bookableItemData);
    }

    // Update translations for BookableItem
    if (ticketData && ticketData.length > 0) {
      for (const data of ticketData) {
        await prisma.bookableItemTranslations.upsert({
          where: {
            bookableItemId_languageId: {
              bookableItemId: currentTicket.bookableItemId,
              languageId: data.languageId,
            },
          },
          create: {
            bookableItemId: currentTicket.bookableItemId,
            languageId: data.languageId,
            name: data.name,
            description: data.description,
            saleEndedMessage: data.saleEndedMessage,
            beforeSaleStartMessage: data.beforeSaleStartMessage,
            buyButtonText: data.buyButtonText,
            soldOutMessage: data.soldOutMessage,
          },
          update: {
            name: data.name,
            description: data.description,
            saleEndedMessage: data.saleEndedMessage,
            beforeSaleStartMessage: data.beforeSaleStartMessage,
            buyButtonText: data.buyButtonText,
            soldOutMessage: data.soldOutMessage,
          },
        });
      }
    }

    // Prepare ticket-specific update data
    let ticketUpdateData: any = { ...ticketSpecificData };

    if (categoryId) {
      ticketUpdateData.category = {
        connect: { id: categoryId },
      };
    } else {
      ticketUpdateData.category = {
        disconnect: true,
      };
    }

    const ticket = await prisma.ticket.update({
      where: {
        id,
      },
      data: ticketUpdateData,
      include: {
        bookableItem: {
          include: {
            translations: true,
            designs: true,
            zones: true,
            participantGroups: true,
          },
        },
      },
    });

    await invalidateEventPageCacheForEvent(currentTicket.eventId);

    // Return flattened structure for backward compatibility
    return flattenTicketWithBookableItem(ticket as any);
  } catch (error) {
    throw error;
  }
};

// DELETE
/**
 * Soft delete a ticket and its associated BookableItem
 * Uses transaction to ensure atomicity
 */
export const deleteTicket = async (userId: string, id: string) => {
  try {
    return await prisma.$transaction(async (tx) => {
      const ticket = await tx.ticket.findUnique({
        where: { id },
        select: {
          id: true,
          eventId: true,
          bookableItemId: true,
          bookableItem: {
            select: {
              saleStartDate: true,
              saleEndDate: true,
            },
          },
          orderItems: {
            select: {
              id: true,
              order: {
                select: {
                  status: true,
                },
              },
            },
          },
        },
      });

      if (!ticket) {
        throw new Error("Ticket not found");
      }

      // Sale active rule: active iff now is between start/end (end null means ongoing).
      const now = new Date();
      const saleStartDate = ticket.bookableItem?.saleStartDate;
      const saleEndDate = ticket.bookableItem?.saleEndDate;
      const isSaleActive = !!(
        saleStartDate &&
        saleStartDate <= now &&
        (!saleEndDate || now <= saleEndDate)
      );

      if (isSaleActive) {
        throw new Error("Cannot delete ticket: Ticket sale is active.");
      }

      if (ticket.orderItems.length > 0) {
        throw new Error("Cannot delete ticket: It has associated orders.");
      }

      // Soft-delete the ticket
      const deletedTicket = await tx.ticket.update({
        where: { id },
        data: { deletedAt: now },
      });

      // Soft-delete the associated BookableItem
      await tx.bookableItem.update({
        where: { id: ticket.bookableItemId },
        data: { deletedAt: now },
      });

      await invalidateEventPageCacheForEvent(ticket.eventId);

      return deletedTicket;
    });
  } catch (error: any) {
    throw error;
  }
};

/**
 * End ticket sale by setting saleEndDate to current date
 * Updates BookableItem (migrated)
 */
export const endTicketSale = async (userId: string, id: string) => {
  try {
    const ticket = await prisma.ticket.findUnique({
      where: { id },
      select: { bookableItemId: true },
    });

    if (!ticket) {
      throw new Error("Ticket not found");
    }

    // Update BookableItem saleEndDate
    await updateBookableItem(ticket.bookableItemId, {
      saleEndDate: new Date(),
    });

    // Return ticket with bookableItem for backward compatibility
    const updatedTicket = await prisma.ticket.findUnique({
      where: { id },
      include: {
        bookableItem: {
          include: {
            translations: true,
            designs: true,
            zones: true,
            participantGroups: true,
          },
        },
      },
    });

    return flattenTicketWithBookableItem(updatedTicket as any);
  } catch (error: any) {
    throw error;
  }
};

export enum BY_REQUEST_TABS {
  All = "all",
  New = "new",
  Approved = "approved",
  Rejected = "rejected",
  RequireRevision = "requireRevision",
}

export const ticketByRequestReport = async (
  eventId: string,
  page = 1,
  limit = 10,
  lang = "en",
  paginate = false,
  type?: BY_REQUEST_TABS,
  deselectedColumns?: any,
  exported?: boolean,
  search?: string,
  filters?: Record<string, string>
) => {
  try {
    const prisma = exported ? exportPrisma : reportingPrisma;
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const searchAsNumber = !isNaN(Number(search)) ? Number(search) : null;
    const searchAsString = search ? search.toString() : null;
    const footerStages = [
      {
        $facet: {
          pagination: [
            {
              $group: {
                _id: null,
                total: { $sum: 1 },
              },
            },
            {
              $addFields: {
                totalPages: { $ceil: { $divide: ["$total", limit] } }, // Calculate total pages based on limit

                hasNextPage: {
                  $lt: [page, { $ceil: { $divide: ["$total", limit] } }],
                },
              },
            },
          ],
          result: [
            ...(paginate
              ? [
                  {
                    $skip: limit * (page - 1),
                  },
                  {
                    $limit: limit,
                  },
                ]
              : []),
          ],
        },
      },
    ];
    const filtrationStages: any[] = [];
    const searchStages: any[] = [];
    const filterStages: any[] = [];

    switch (type) {
      case BY_REQUEST_TABS.New:
        filtrationStages.push({
          $match: { "orderItem.status": OrderItemStatus.PENDING_APPROVAL },
        });
        break;

      case BY_REQUEST_TABS.Approved:
        filtrationStages.push({
          $match: { "orderItem.status": OrderItemStatus.APPROVED },
        });
        break;

      case BY_REQUEST_TABS.Rejected:
        filtrationStages.push({
          $match: { "orderItem.status": OrderItemStatus.REJECTED },
        });
        break;

      case BY_REQUEST_TABS.RequireRevision:
        filtrationStages.push({
          $match: { "orderItem.status": OrderItemStatus.REQUEST_REVISION },
        });
        break;

      case BY_REQUEST_TABS.All:
      default:
        // No filtration needed
        break;
    }

    if (search) {
      searchStages.push({
        $match: {
          $or: [
            { "order.number": searchAsNumber },
            {
              $expr: {
                $regexMatch: {
                  regex: searchAsString,
                  input: {
                    $reduce: {
                      input: {
                        $ifNull: ["$orderItems.translations.name", []], // Handle null or missing arrays
                      },
                      initialValue: "",
                      in: {
                        $concat: ["$$value", " ", { $toString: "$$this" }], // Flatten and convert array to string
                      },
                    },
                  },
                  options: "im",
                },
              },
            },
            {
              $expr: {
                $regexMatch: {
                  regex: searchAsString,
                  input: {
                    $reduce: {
                      input: {
                        $objectToArray:
                          "$orderFormResultEntity.formResult.surveyResult",
                      },
                      initialValue: "",
                      in: {
                        $concat: ["$$value", " ", { $toString: "$$this.v" }],
                      },
                    },
                  },
                  options: "im",
                },
              },
            },
          ].filter(Boolean),
        },
      });
    }

    // Add dynamic field filtering
    if (filters && Object.keys(filters).length > 0) {
      const filterConditions: any[] = [];

      Object.entries(filters).forEach(([fieldName, fieldValue]) => {
        if (fieldValue && fieldValue.trim() !== "") {
          // Check if the filter value is a number
          const isNumeric = !isNaN(Number(fieldValue));
          const numericValue = isNumeric ? Number(fieldValue) : null;

          // Create filter conditions for both order and ticket form results
          const baseConditions = [
            // Filter in order form results - string match
            {
              [`orderFormResultEntity.formResult.surveyResult.${fieldName}`]: {
                $regex: fieldValue,
                $options: "i",
              },
            },
            // Filter in ticket form results - string match
            {
              [`formResultEntity.formResult.surveyResult.${fieldName}`]: {
                $regex: fieldValue,
                $options: "i",
              },
            },
          ];

          // If the value is numeric, add numeric matching conditions
          if (isNumeric && numericValue !== null) {
            baseConditions.push(
              // Filter in order form results - exact numeric match
              {
                [`orderFormResultEntity.formResult.surveyResult.${fieldName}`]:
                  { $regex: `^${numericValue}$`, $options: "i" },
              },
              // Filter in ticket form results - exact numeric match
              {
                [`formResultEntity.formResult.surveyResult.${fieldName}`]: {
                  $regex: `^${numericValue}$`,
                  $options: "i",
                },
              }
            );
          }

          filterConditions.push({
            $or: baseConditions,
          });
        }
      });

      if (filterConditions.length > 0) {
        filterStages.push({
          $match: {
            $and: filterConditions,
          },
        });
      }
    }

    const stages = [
      {
        $lookup: {
          from: "BookableItem",
          localField: "bookableItemId",
          foreignField: "_id",
          as: "bookableItem",
        },
      },
      { $unwind: { path: "$bookableItem", preserveNullAndEmptyArrays: true } },
      {
        $match: {
          "bookableItem.requireApproval": true,
        },
      },

      { $sort: { _id: -1 } },
      {
        $match: {
          eventId: { $oid: eventId },
        },
      },
      {
        $lookup: {
          from: "OrderItem",
          localField: "_id",
          foreignField: "ticketId",
          as: "orderItem",
        },
      },
      {
        $unwind: {
          path: "$orderItem",
          preserveNullAndEmptyArrays: true,
        },
      },
      ...filtrationStages,
      {
        $lookup: {
          from: "Order",
          localField: "orderItem.orderId",
          foreignField: "_id",
          as: "order",
        },
      },
      {
        $unwind: {
          path: "$order",
          preserveNullAndEmptyArrays: true,
        },
      },
      {
        $lookup: {
          from: "Payment",
          localField: "order._id",
          foreignField: "orderId",
          as: "payment",
        },
      },
      {
        $match: {
          "order.status": OrderStatus.COMPLETED,
          "order.eventId": { $oid: eventId },
        },
      },
      { $addFields: { "orderItem.id": { $toString: "$orderItem._id" } } },

      {
        $lookup: {
          from: "BookableItemTranslations",
          localField: "bookableItem._id",
          foreignField: "bookableItemId",
          as: "ticket.translations",
          pipeline: [
            {
              $lookup: {
                from: "Language",
                localField: "languageId",
                foreignField: "_id",
                as: "language",
              },
            },
            {
              $match: {
                "language.code": lang,
              },
            },
            {
              $project: {
                _id: 0,
                name: 1,
                description: 1,
              },
            },
          ],
        },
      },
      {
        $addFields: {
          "orderItem.translations": "$ticket.translations",
          "orderItem.color": "$bookableItem.color",
          orderItemIdAsString: { $toString: "$orderItem._id" },
        },
      },
      {
        $lookup: {
          from: "FormResultEntity",
          localField: "orderItem._id",
          foreignField: "orderItemId",
          as: "formResultEntity",
        },
      },
      {
        $unwind: {
          path: "$formResultEntity",
          preserveNullAndEmptyArrays: true,
        },
      },
      {
        $lookup: {
          from: "FormResult",
          localField: "formResultEntity.formResultId",
          foreignField: "_id",
          as: "formResultEntity.formResult",
        },
      },
      {
        $unwind: {
          path: "$formResultEntity.formResult",
          preserveNullAndEmptyArrays: true,
        },
      },
      {
        $lookup: {
          from: "FormTemplate",
          localField: "formResultEntity.formResult.formTemplateId",
          foreignField: "_id",
          as: "formResultEntity.formTemplate",
        },
      },
      {
        $unwind: {
          path: "$formResultEntity.formTemplate",
          preserveNullAndEmptyArrays: true,
        },
      },
      {
        $addFields: {
          orderId: { $toString: "$order._id" },
        },
      },
      {
        $lookup: {
          from: "FormResultEntity",
          localField: "order._id",
          foreignField: "orderId",
          as: "orderFormResultEntity",
        },
      },
      {
        $unwind: {
          path: "$orderFormResultEntity",
          preserveNullAndEmptyArrays: true,
        },
      },
      {
        $lookup: {
          from: "FormResult",
          localField: "orderFormResultEntity.formResultId",
          foreignField: "_id",
          as: "orderFormResultEntity.formResult",
        },
      },
      {
        $unwind: {
          path: "$orderFormResultEntity.formResult",
          preserveNullAndEmptyArrays: true,
        },
      },
      {
        $lookup: {
          from: "FormTemplate",
          localField: "orderFormResultEntity.formResult.formTemplateId",
          foreignField: "_id",
          as: "orderFormResultEntity.formResult.formTemplate",
        },
      },
      {
        $unwind: {
          path: "$orderFormResultEntity.formResult.formTemplate",
          preserveNullAndEmptyArrays: true,
        },
      },
      ...searchStages,
      ...filterStages,
      {
        $group: {
          _id: "$order._id",
          Status: { $first: "$order.status" },
          orderItemsStatus: { $addToSet: "$orderItem.status" },
          paymentStatuses: {
            $addToSet: "$payment.status",
          },
          orderItems: { $push: "$orderItem" },
          "Linked Ticket": {
            $push: {
              _id: "$_id",
              name: { $arrayElemAt: ["$orderItem.translations.name", 0] },
              bookableItem: {
                color: "$orderItem.color",
              },
            },
          },
          "Receiving Date": {
            $first: {
              $dateToString: {
                format: "%Y-%m-%d %H:%M:%S",
                date: "$order.updatedAt",
                timezone: userTimeZone,
              },
            },
          },
          profileId: {
            $first: "$profileId",
          },
          formResultEntity: {
            $push: "$formResultEntity",
          },
          orderFormResultEntity: {
            $first: "$orderFormResultEntity",
          },
          orderId: {
            $first: "$order._id",
          },
          orderNumber: {
            $first: "$order.number",
          },
        },
      },
      { $sort: { "Receiving Date": -1 } },
      {
        $addFields: {
          holderEmails: {
            $map: {
              input: "$formResultEntity",
              as: "entity",
              in: {
                $let: {
                  vars: {
                    emailField: {
                      $arrayElemAt: [
                        {
                          $filter: {
                            input: {
                              $arrayElemAt: [
                                "$$entity.formTemplate.json.pages.elements",
                                0,
                              ],
                            },
                            as: "element",
                            cond: { $eq: ["$$element.type", "email"] },
                          },
                        },
                        0,
                      ],
                    },
                  },
                  in: {
                    $cond: [
                      {
                        $and: [
                          { $ne: ["$$emailField", null] },
                          {
                            $ne: [
                              {
                                $type: "$$entity.formResult.surveyResult.email",
                              },
                              "missing",
                            ],
                          },
                        ],
                      },
                      "$$entity.formResult.surveyResult.email",
                      null,
                    ],
                  },
                },
              },
            },
          },
          holderNames: {
            $map: {
              input: "$formResultEntity",
              as: "entity",
              in: {
                $let: {
                  vars: {
                    nameField: {
                      $arrayElemAt: [
                        {
                          $filter: {
                            input: {
                              $arrayElemAt: [
                                "$$entity.formTemplate.json.pages.elements",
                                0,
                              ],
                            },
                            as: "element",
                            cond: { $eq: ["$$element.type", "name"] },
                          },
                        },
                        0,
                      ],
                    },
                  },
                  in: {
                    $cond: [
                      {
                        $and: [
                          { $ne: ["$$nameField", null] },
                          {
                            $ne: [
                              {
                                $type: "$$entity.formResult.surveyResult.name",
                              },
                              "missing",
                            ],
                          },
                        ],
                      },
                      "$$entity.formResult.surveyResult.name",
                      null,
                    ],
                  },
                },
              },
            },
          },
          holderPhones: {
            $map: {
              input: "$formResultEntity",
              as: "entity",
              in: {
                $let: {
                  vars: {
                    phoneField: {
                      $arrayElemAt: [
                        {
                          $filter: {
                            input: {
                              $arrayElemAt: [
                                "$$entity.formTemplate.json.pages.elements",
                                0,
                              ],
                            },
                            as: "element",
                            cond: { $eq: ["$$element.type", "phone"] },
                          },
                        },
                        0,
                      ],
                    },
                  },
                  in: {
                    $cond: [
                      {
                        $and: [
                          { $ne: ["$$phoneField", null] },
                          {
                            $ne: [
                              {
                                $type: "$$entity.formResult.surveyResult.phone",
                              },
                              "missing",
                            ],
                          },
                        ],
                      },
                      "$$entity.formResult.surveyResult.phone",
                      null,
                    ],
                  },
                },
              },
            },
          },
          holderCompanies: {
            $map: {
              input: "$formResultEntity",
              as: "entity",
              in: {
                $let: {
                  vars: {
                    companyField: {
                      $arrayElemAt: [
                        {
                          $filter: {
                            input: {
                              $arrayElemAt: [
                                "$$entity.formTemplate.json.pages.elements",
                                0,
                              ],
                            },
                            as: "element",
                            cond: { $eq: ["$$element.type", "company"] },
                          },
                        },
                        0,
                      ],
                    },
                  },
                  in: {
                    $cond: [
                      {
                        $and: [
                          { $ne: ["$$companyField", null] },
                          {
                            $ne: [
                              {
                                $type:
                                  "$$entity.formResult.surveyResult.company",
                              },
                              "missing",
                            ],
                          },
                        ],
                      },
                      "$$entity.formResult.surveyResult.company",
                      null,
                    ],
                  },
                },
              },
            },
          },
          isFinalStatus: {
            $in: [
              "$result.order.status",
              ["CANCELED", "EXPIRED", "DELETED", "ON_GOING"],
            ],
          },
          hasPendingApproval: {
            $in: ["PENDING_APPROVAL", "$orderItemsStatus"],
          },
          isPartiallyApproved: {
            $and: [
              {
                $in: ["APPROVED", "$orderItemsStatus"],
              },
              {
                $in: ["PENDING_APPROVAL", "$orderItemsStatus"],
              },
            ],
          },
          allConfirmed: {
            $and: [
              { $gt: [{ $size: "$orderItems" }, 0] },
              {
                $allElementsTrue: {
                  $map: {
                    input: "$orderItems",
                    as: "item",
                    in: { $eq: ["$$item.status", "CONFIRMED"] },
                  },
                },
              },
            ],
          },
          // Paid / partially-paid / unpaid derivation shared with
          // `calculateOrderStatus`: ignores FAILED/EXPIRED retry attempts so a
          // `[FAILED, PAID]` order is reported as PAID/COMPLETED.
          ...buildPaymentSettlementExpressions("$paymentStatuses"),
          allRejected: {
            $and: [
              { $gt: [{ $size: "$orderItemsStatus" }, 0] },
              {
                $eq: [
                  {
                    $size: {
                      $setIntersection: ["$orderItemsStatus", ["REJECTED"]],
                    },
                  },
                  { $size: "$orderItemsStatus" },
                ],
              },
            ],
          },
          allRequestRevision: {
            $and: [
              { $gt: [{ $size: "$orderItemsStatus" }, 0] },
              {
                $eq: [
                  {
                    $size: {
                      $setIntersection: [
                        "$orderItemsStatus",
                        ["REQUEST_REVISION"],
                      ],
                    },
                  },
                  { $size: "$orderItemsStatus" },
                ],
              },
            ],
          },
          allApproved: {
            $and: [
              { $gt: [{ $size: "$orderItemsStatus" }, 0] },
              {
                $eq: [
                  {
                    $size: {
                      $setIntersection: ["$orderItemsStatus", ["APPROVED"]],
                    },
                  },
                  { $size: "$orderItemsStatus" },
                ],
              },
            ],
          },
        },
      },

      {
        $addFields: {
          Holder: {
            $reduce: {
              input: {
                $filter: {
                  input: "$Holders",
                  as: "item",
                  cond: { $ne: ["$$item", null] },
                },
              },
              initialValue: "",
              in: {
                $cond: [
                  { $eq: ["$$value", ""] },
                  "$$this",
                  { $concat: ["$$value", ", ", "$$this"] },
                ],
              },
            },
          },
        },
      },
      {
        $addFields: {
          Holder: {
            $reduce: {
              input: {
                $filter: {
                  input: "$Holders",
                  as: "item",
                  cond: { $ne: ["$$item", null] },
                },
              },
              initialValue: "",
              in: {
                $cond: [
                  { $eq: ["$$value", ""] },
                  "$$this",
                  { $concat: ["$$value", ", ", "$$this"] },
                ],
              },
            },
          },
          buyerEmail: {
            $let: {
              vars: {
                emailField: {
                  $arrayElemAt: [
                    {
                      $filter: {
                        input: {
                          $objectToArray:
                            "$orderFormResultEntity.formResult.surveyResult",
                        },
                        as: "result",
                        cond: {
                          $eq: ["$$result.k", "email"],
                        },
                      },
                    },
                    0,
                  ],
                },
              },
              in: {
                $ifNull: ["$$emailField.v", ""],
              },
            },
          },
          buyerName: {
            $let: {
              vars: {
                nameField: {
                  $arrayElemAt: [
                    {
                      $filter: {
                        input: {
                          $objectToArray:
                            "$orderFormResultEntity.formResult.surveyResult",
                        },
                        as: "result",
                        cond: {
                          $eq: ["$$result.k", "name"],
                        },
                      },
                    },
                    0,
                  ],
                },
              },
              in: {
                $ifNull: ["$$nameField.v", ""],
              },
            },
          },
          buyerPhone: {
            $let: {
              vars: {
                phoneField: {
                  $arrayElemAt: [
                    {
                      $filter: {
                        input: {
                          $objectToArray:
                            "$orderFormResultEntity.formResult.surveyResult",
                        },
                        as: "result",
                        cond: {
                          $eq: ["$$result.k", "phone"],
                        },
                      },
                    },
                    0,
                  ],
                },
              },
              in: {
                $ifNull: ["$$phoneField.v", ""],
              },
            },
          },
          buyerCompany: {
            $let: {
              vars: {
                companyField: {
                  $arrayElemAt: [
                    {
                      $filter: {
                        input: {
                          $objectToArray:
                            "$result.orderFormResultEntity.formResult.surveyResult",
                        },
                        as: "result",
                        cond: {
                          $eq: ["$$result.k", "company"],
                        },
                      },
                    },
                    0,
                  ],
                },
              },
              in: {
                $ifNull: ["$$companyField.v", ""],
              },
            },
          },
        },
      },
      {
        $addFields: {
          buyerEmail: {
            $cond: {
              if: { $eq: ["$buyerEmail", ""] },
              then: {
                $reduce: {
                  input: {
                    $filter: {
                      input: "$holderEmails",
                      as: "email",
                      cond: { $ne: ["$$email", null] },
                    },
                  },
                  initialValue: "",
                  in: {
                    $cond: [
                      { $eq: ["$$value", ""] },
                      "$$this",
                      { $concat: ["$$value", ", ", "$$this"] },
                    ],
                  },
                },
              },
              else: {
                $concat: [
                  "$buyerEmail",
                  ", ",
                  {
                    $reduce: {
                      input: {
                        $filter: {
                          input: "$holderEmails",
                          as: "email",
                          cond: { $ne: ["$$email", null] },
                        },
                      },
                      initialValue: "",
                      in: {
                        $cond: [
                          { $eq: ["$$value", ""] },
                          "$$this",
                          { $concat: ["$$value", ", ", "$$this"] },
                        ],
                      },
                    },
                  },
                ],
              },
            },
          },
          buyerName: {
            $cond: {
              if: { $eq: ["$buyerName", ""] },
              then: {
                $reduce: {
                  input: {
                    $filter: {
                      input: "$holderNames",
                      as: "name",
                      cond: { $ne: ["$$name", null] },
                    },
                  },
                  initialValue: "",
                  in: {
                    $cond: [
                      { $eq: ["$$value", ""] },
                      "$$this",
                      { $concat: ["$$value", ", ", "$$this"] },
                    ],
                  },
                },
              },
              else: {
                $concat: [
                  "$buyerName",
                  ", ",
                  {
                    $reduce: {
                      input: {
                        $filter: {
                          input: "$holderNames",
                          as: "name",
                          cond: { $ne: ["$$name", null] },
                        },
                      },
                      initialValue: "",
                      in: {
                        $cond: [
                          { $eq: ["$$value", ""] },
                          "$$this",
                          { $concat: ["$$value", ", ", "$$this"] },
                        ],
                      },
                    },
                  },
                ],
              },
            },
          },
          orderDynamicField: {
            $arrayToObject: {
              $objectToArray: "$orderFormResultEntity.formResult.surveyResult",
            },
          },
          ticketDynamicField: {
            $mergeObjects: "$formResultEntity.formResult.surveyResult",
          },
        },
      },
      {
        $addFields: {
          virtualStatus: {
            $cond: {
              if: "$isFinalStatus",
              then: "$result.order.status",
              else: {
                $cond: {
                  if: "$hasPendingApproval",
                  then: "PENDING_APPROVAL",
                  else: {
                    $cond: {
                      if: "$isPartiallyApproved",
                      then: "PARTIALLY_APPROVED",
                      else: {
                        $cond: {
                          if: { $and: ["$allConfirmed", "$allPaid"] },
                          then: "COMPLETED",
                          else: {
                            $cond: {
                              if: { $and: ["$allConfirmed", "$anyPaid"] },
                              then: "PARTIALLY_PAID",
                              else: {
                                $cond: {
                                  if: "$allRejected",
                                  then: "REJECTED",
                                  else: {
                                    $cond: {
                                      if: "$allRequestRevision",
                                      then: "REQUEST_REVISION",
                                      else: {
                                        $cond: {
                                          if: "$allApproved",
                                          then: "APPROVED",
                                          else: {
                                            $cond: {
                                              if: "$allPaid",
                                              then: "PAID",
                                              else: {
                                                $cond: {
                                                  if: "$anyPaid",
                                                  then: "PARTIALLY_PAID",
                                                  else: {
                                                    $cond: {
                                                      if: "$allUnpaid",
                                                      then: "AWAITING_PAYMENT",
                                                      else: {
                                                        $cond: {
                                                          if: "$hasOnlySupersededPayments",
                                                          then: "$supersededPaymentStatus",
                                                          else: "ON_GOING",
                                                        },
                                                      },
                                                    },
                                                  },
                                                },
                                              },
                                            },
                                          },
                                        },
                                      },
                                    },
                                  },
                                },
                              },
                            },
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      },
      {
        $project: {
          orderId: {
            $toString: "$orderId",
          },
          orderDynamicField: 1,
          ticketDynamicField: 1,
          orderNumber: 1,
          Name: {
            $cond: {
              if: {
                $and: [
                  { $ne: ["$buyerName", null] },
                  { $ne: ["$buyerName", ""] },
                ],
              },
              then: "$buyerName",
              else: {
                $cond: {
                  if: {
                    $and: [
                      { $ne: ["$result.contact.name", null] },
                      { $ne: ["$result.contact.name", ""] },
                    ],
                  },
                  then: "$result.contact.name",
                  else: "",
                },
              },
            },
          },
          Email: {
            $cond: {
              if: {
                $and: [
                  { $ne: ["$buyerEmail", null] },
                  { $ne: ["$buyerEmail", ""] },
                ],
              },
              then: "$buyerEmail",
              else: {
                $cond: {
                  if: {
                    $and: [
                      { $ne: ["$result.contact.email", null] },
                      { $ne: ["$result.contact.email", ""] },
                    ],
                  },
                  then: "$result.contact.email",
                  else: "",
                },
              },
            },
          },
          orderFormResultEntity: 1,
          formResultEntity: 1,
          "Order Item Status": "$orderItemsStatus",
          orderItems: 1,
          Status: "$virtualStatus",
          "Linked Ticket": 1,
          "Receiving Date": 1,
          formResultOrder: 1,
          formResultOrderItem: 1,
          profile: {
            name: 1,
            email: 1,
            phoneNumber: 1,
            countryCode: 1,
          },
        },
      },
      {
        $replaceRoot: {
          newRoot: {
            $mergeObjects: [
              "$$ROOT",
              "$orderDynamicField",
              "$ticketDynamicField",
            ],
          },
        },
      },
      {
        $project: {
          orderDynamicField: 0,
          ticketDynamicField: 0,
        },
      },
      ...(deselectedColumns || exported
        ? [
            {
              $addFields: {
                OrderItemStatus: {
                  $reduce: {
                    input: "$Order Item Status",
                    initialValue: "",
                    in: {
                      $cond: {
                        if: { $eq: ["$$value", ""] },
                        then: "$$this",
                        else: { $concat: ["$$value", ", ", "$$this"] },
                      },
                    },
                  },
                },
                LinkedTicket: {
                  $reduce: {
                    input: {
                      $map: {
                        input: "$Linked Ticket",
                        as: "ticket",
                        in: "$$ticket.name",
                      },
                    },
                    initialValue: "",
                    in: {
                      $cond: {
                        if: { $eq: ["$$value", ""] },
                        then: "$$this",
                        else: { $concat: ["$$value", ", ", "$$this"] },
                      },
                    },
                  },
                },
              },
            },
            {
              $project: {
                _id: 0,
                orderItems: 0,
                formResultOrderItem: 0,
                formResultOrder: 0,
                "Order Item Status": 0,
                "Linked Ticket": 0,
                ...deselectedColumns,
              },
            },
          ]
        : []),
      ...footerStages,
    ];
    const result = await prisma.ticket.aggregateRaw({
      pipeline: [...stages],
    });
    return result[0];
  } catch (e: any) {
    throw Error(e.message);
  }
};

export enum TICKET_TYPE {
  PUBLIC = "PUBLIC",
  INVITATION = "INVITATION",
  BY_REQUEST = "BY_REQUEST",
  ALL = "ALL",
}

/**
 * Get tickets from multiple events (MIGRATED)
 * Filter by privacy and requireApproval via bookableItem
 * Returns flattened structure for backward compatibility
 */
export const getTicketsFromEvents = async (
  eventIds: string[],
  ticketType: TICKET_TYPE = TICKET_TYPE.ALL
) => {
  try {
    let where: Prisma.TicketWhereInput = {
      eventId: {
        in: eventIds,
      },
      deletedAt: { isSet: false },
    };

    if (ticketType === TICKET_TYPE.BY_REQUEST) {
      where.bookableItem = {
        requireApproval: true,
      };
    } else if (ticketType === TICKET_TYPE.INVITATION) {
      where.bookableItem = {
        privacy: Privacies.INVITATION,
      };
    } else if (ticketType === TICKET_TYPE.PUBLIC) {
      where.bookableItem = {
        privacy: Privacies.PUBLIC,
      };
    }

    const tickets = await prisma.ticket.findMany({
      where,
      include: {
        bookableItem: {
          include: {
            translations: true,
          },
        },
      },
    });

    return tickets.map((ticket) =>
      flattenTicketWithBookableItem(ticket as any)
    );
  } catch (error: any) {
    throw error;
  }
};

/**
 * Get design templates for a ticket
 * NOTE: Migrated to use BookableItem.designs (DesignOnTickets removed)
 */
export async function getTicketTemplatesByTicketId(ticketId: string) {
  try {
    // Get ticket with bookableItem and designs
    const ticket = await prisma.ticket.findUnique({
      where: { id: ticketId },
      select: {
        bookableItem: {
          select: {
            designs: {
              include: {
                translations: true,
              },
            },
          },
        },
      },
    });

    if (!ticket?.bookableItem) {
      return [];
    }

    // Return designs in format similar to old DesignOnTickets structure
    // for backward compatibility
    return ticket.bookableItem.designs.map((design) => ({
      design,
      ticketId,
      designId: design.id,
    }));
  } catch (error: any) {
    throw new Error(`Failed to get ticket templates: ${error.message}`);
  }
}
